import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addDiscipline,
  deleteDiscipline,
  getAllDisciplines,
  updateDiscipline as updateDisciplineApi,
  uploadImage,
} from "../../../../../services/disciplinesServices";

import {
  buildDisciplinesStats,
  filterAndSortDisciplines,
  isDemoUser,
  normalizeDisciplinePayload,
  safeJsonParse,
} from "../utils/disciplinesUtils";

const INITIAL_FILTERS = {
  query: "",
  sortOrder: "ordre_asc",
};

export default function useDisciplines() {
  const [disciplines, setDisciplines] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshDisciplines = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllDisciplines();
      setDisciplines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des disciplines :", error);
      toast.error("Erreur lors de la récupération des disciplines.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshDisciplines();
  }, [loadUserMode, refreshDisciplines]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const uploadDisciplineImage = useCallback(async (imageFile) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("category", "disciplines");

    const uploadResponse = await uploadImage(formData);

    return (
      uploadResponse?.filePath ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      null
    );
  }, []);

  const buildPayloadWithImage = useCallback(
    async (formData) => {
      const payload = normalizeDisciplinePayload(formData);

      if (formData.imageFile) {
        const uploadedImageUrl = await uploadDisciplineImage(formData.imageFile);

        if (uploadedImageUrl) {
          payload.image_discipline = uploadedImageUrl;
        }
      }

      delete payload.imageFile;

      return payload;
    },
    [uploadDisciplineImage]
  );

  const filteredDisciplines = useMemo(
    () => filterAndSortDisciplines(disciplines, filters),
    [disciplines, filters]
  );

  const stats = useMemo(
    () => buildDisciplinesStats(disciplines, filteredDisciplines),
    [disciplines, filteredDisciplines]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedDiscipline(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (discipline) => {
      if (guardDemoMode()) return;

      setSelectedDiscipline(discipline);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((discipline) => {
    setSelectedDiscipline(discipline);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedDiscipline(null);
  }, []);

  const createDiscipline = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await addDiscipline(payload);

        toast.success("Discipline créée avec succès.");
        closeDrawer();
        await refreshDisciplines();
      } catch (error) {
        console.error("Erreur lors de la création de la discipline :", error);
        toast.error("Erreur lors de la création de la discipline.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshDisciplines]
  );

  const updateDiscipline = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await updateDisciplineApi(payload);

        toast.success("Discipline modifiée avec succès.");
        closeDrawer();
        await refreshDisciplines();
      } catch (error) {
        console.error("Erreur lors de la modification de la discipline :", error);
        toast.error("Erreur lors de la modification de la discipline.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshDisciplines]
  );

  const askDelete = useCallback(
    (disciplineId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(disciplineId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (disciplineId) => {
      if (guardDemoMode()) return;

      try {
        await deleteDiscipline(disciplineId);

        toast.success("Discipline supprimée avec succès.");
        setDeleteCandidateId(null);
        await refreshDisciplines();
      } catch (error) {
        console.error("Erreur lors de la suppression de la discipline :", error);
        toast.error("Erreur lors de la suppression de la discipline.");
      }
    },
    [guardDemoMode, refreshDisciplines]
  );

  return {
    loading,
    saving,
    filters,
    setFilter,
    disciplines,
    filteredDisciplines,
    selectedDiscipline,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshDisciplines,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createDiscipline,
      updateDiscipline,
      askDelete,
      cancelDelete,
      confirmDelete,
    },
  };
}